import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInAnonymously, signInWithCustomToken, updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, addDoc, onSnapshot, query, where, updateDoc, deleteDoc, writeBatch, getDocs } from 'firebase/firestore';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';


// --- Color Palette ---
const COLORS = {
    primary: '#0A3D62', // Dark Blue
    secondary: '#228B22', // Forest Green
    accent: '#F96716',  // Orange
};

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// --- Firebase Configuration ---
const firebaseConfig = {
    apiKey: "AIzaSyCOX6N7f3w4-ycvYTr_vLr-HDU5nLfwWJk",
    authDomain: "fsw-comply-prod.firebaseapp.com",
    projectId: "fsw-comply-prod",
    storageBucket: "fsw-comply-prod.appspot.com",
    messagingSenderId: "3868970309",
    appId: "1:3868970309:web:1e1202feaca6ae5f46022c"
};

// --- App ID ---
const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'fsw-comply-mvp';

// --- Firebase Initialization ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// --- Helper Functions & Constants ---
const REGULATION_OPTIONS = [
    "National Building Code (NBC) 2016, Part 4",
    "Maharashtra Fire Prevention and Life Safety Measures Act, 2006",
    "Delhi Fire Service Act, 2007",
    "Gujarat Fire Prevention and Life Safety Measures Act, 2013",
    "Karnataka Fire Force Act, 1964",
    "Tamil Nadu Fire Service Act, 1985",
    "IS 2189: Selection, Installation and Maintenance of Automatic Fire Detection and Alarm System",
    "IS 15683: Portable Fire Extinguishers — Performance and Construction"
];

const ASSET_TYPES = [
    "Fire Extinguisher", "Smoke Detector", "Heat Detector", "Fire Alarm Panel",
    "Sprinkler Head", "Fire Hydrant", "Hose Reel", "Emergency Light"
];

// --- Icon Components ---
const Icon = ({ path, className = "w-6 h-6" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d={path} />
    </svg>
);

const ICONS = {
    dashboard: "M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z",
    compliance: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 12.17l7.59-7.59L19 6l-9 9z",
    assets: "M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z",
    emergency: "M10.02 21.02c.49.49 1.28.49 1.77 0l8.49-8.49c.49-.49.49-1.28 0-1.77l-2.12-2.12-5.66 5.66-2.83-2.83-5.66 5.66 2.12 2.12 4.24-4.24 1.41 1.41-4.24 4.24-1.41-1.41-1.41 1.41 4.24 4.24z",
    add: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z",
    edit: "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z",
    delete: "M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z",
    close: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z",
    user: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
    camera: "M12 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm-2 4h4v-1.03c-1.23-.3-2.77-.3-4 0V16zm8-4c0-2.21-1.79-4-4-4S8 7.79 8 10s1.79 4 4 4 4-1.79 4-4zM4.94 4.94C3.53 6.35 2.5 8.32 2.5 10.5S3.53 14.65 4.94 16.06c1.41 1.41 3.38 2.44 5.56 2.44s4.15-1.03 5.56-2.44c1.41-1.41 2.44-3.38 2.44-5.56s-1.03-4.15-2.44-5.56C14.65 3.53 12.68 2.5 10.5 2.5S6.35 3.53 4.94 4.94z"
};

// --- Reusable Confirmation Modal ---
const ConfirmationModal = ({ show, title, message, onConfirm, onCancel, theme }) => {
    if (!show) return null;
    const isDark = theme === 'dark';
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-2xl p-8 w-full max-w-md`}>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>{title}</h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>{message}</p>
                <div className="flex justify-end space-x-4">
                    <button onClick={onCancel} className="py-2 px-4 bg-gray-600 hover:bg-gray-500 rounded-lg text-white font-semibold">Cancel</button>
                    <button onClick={onConfirm} style={{ backgroundColor: COLORS.accent }} className="py-2 px-4 hover:opacity-90 rounded-lg text-white font-semibold">Confirm</button>
                </div>
            </div>
        </div>
    );
};

// --- Mock Data Generation ---
const addMockData = async (userId) => {
    const profileDocRef = doc(db, `artifacts/${appId}/users/${userId}/profile/info`);
    const profileSnap = await getDoc(profileDocRef);
    if (profileSnap.exists()) {
        console.log("Profile and mock data likely exist. Skipping.");
        return;
    }

    console.log("Adding mock data for new user...");
    const batch = writeBatch(db);
    
    // Default Profile
    batch.set(profileDocRef, {
        email: auth.currentUser?.email || 'anonymous',
        createdAt: new Date(),
        appName: 'FSW Comply',
        firstName: 'Demo',
        lastName: 'User',
        companyName: 'FSW Corp',
        photoURL: '',
        theme: 'dark',
        displayNameType: 'firstName'
    });

    // Mock Compliance Docs
    const complianceDocs = [
        { documentName: "Fire Safety Certificate (NOC)", regulation: "Maharashtra Fire Prevention and Life Safety Measures Act, 2006", renewalDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0], fileUrl: "" },
        { documentName: "Building Stability Certificate", regulation: "National Building Code (NBC) 2016, Part 4", renewalDate: new Date(new Date().setDate(new Date().getDate() - 90)).toISOString().split('T')[0], fileUrl: "" },
        { documentName: "Lift and Escalator License", regulation: "Maharashtra Lifts, Escalators and Moving Walks Act, 2017", renewalDate: new Date(new Date().setDate(new Date().getDate() + 25)).toISOString().split('T')[0], fileUrl: "" }
    ];
    complianceDocs.forEach(docData => {
        const docRef = doc(collection(db, `artifacts/${appId}/users/${userId}/compliance`));
        batch.set(docRef, docData);
    });

    // Mock Assets
    const assetsData = [
        { name: "ABC Extinguisher - 1F Lobby", assetType: "Fire Extinguisher", location: "1st Floor Lobby", installDate: "2023-01-10", status: "Operational", uniqueId: `FSW-${Date.now() + 1}` },
        { name: "Smoke Detector - 2F West Wing", assetType: "Smoke Detector", location: "2nd Floor, West Wing", installDate: "2023-01-10", status: "Operational", uniqueId: `FSW-${Date.now() + 2}` },
        { name: "Fire Alarm Panel - Basement", assetType: "Fire Alarm Panel", location: "Basement Control Room", installDate: "2023-01-10", status: "Operational", uniqueId: `FSW-${Date.now() + 3}` },
        { name: "Hose Reel - Parking Level 1", assetType: "Hose Reel", location: "P1 Parking Area", installDate: "2023-01-10", status: "Under Maintenance", uniqueId: `FSW-${Date.now() + 4}` },
    ];
    assetsData.forEach(assetData => {
        const docRef = doc(collection(db, `artifacts/${appId}/users/${userId}/assets`));
        batch.set(docRef, assetData);
    });

    // Mock Audits
    const auditsData = [
        { assetName: "ABC Extinguisher - 1F Lobby", dueDate: new Date(new Date().setDate(new Date().getDate() + 60)).toISOString().split('T')[0], auditor: "SafeZone Auditors", status: "Completed", notes: "Pressure and nozzle checked. OK." },
        { assetName: "Fire Alarm Panel - Basement", dueDate: new Date(new Date().setDate(new Date().getDate() + 120)).toISOString().split('T')[0], auditor: "SafeZone Auditors", status: "Scheduled", notes: "Annual panel checkup." },
        { assetName: "Smoke Detector - 2F West Wing", dueDate: new Date(new Date().setDate(new Date().getDate() - 20)).toISOString().split('T')[0], auditor: "In-house Team", status: "Scheduled", notes: "Quarterly sensor test overdue." }
    ];
    auditsData.forEach(auditData => {
        const docRef = doc(collection(db, `artifacts/${appId}/users/${userId}/audits`));
        batch.set(docRef, auditData);
    });

    // Mock Emergency Plans & Drills
    const planRef = doc(collection(db, `artifacts/${appId}/users/${userId}/plans`));
    batch.set(planRef, { planName: "Main Building Evacuation Plan", assemblyPoint: "North Side Parking Lot", planUrl: "" });

    const drillRef = doc(collection(db, `artifacts/${appId}/users/${userId}/drills`));
    batch.set(drillRef, { drillDate: new Date(new Date().setDate(new Date().getDate() - 180)).toISOString().split('T')[0], participants: 150, evacuationTime: 4.5, observations: "Evacuation was orderly. Some staff confused about secondary exit." });

    await batch.commit();
    console.log("Mock data added successfully.");
};

// --- Main App Component ---
export default function App() {
    const [page, setPage] = useState('dashboard');
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [userId, setUserId] = useState(null);
    const [profile, setProfile] = useState({ theme: 'dark', displayNameType: 'firstName', firstName: '', photoURL: '' });

    useEffect(() => {
        const scriptId = 'tailwind-cdn-script';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://cdn.tailwindcss.com';
            document.head.appendChild(script);
        }
    }, []);
    
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                setUserId(currentUser.uid);
                
                const profileDocRef = doc(db, `artifacts/${appId}/users/${currentUser.uid}/profile/info`);
                
                const unsubProfile = onSnapshot(profileDocRef, async (docSnap) => {
                    if (docSnap.exists()) {
                        setProfile(docSnap.data());
                    } else {
                        await addMockData(currentUser.uid);
                    }
                    setLoading(false);
                });
                
                return () => unsubProfile();
            } else {
                try {
                    const token = typeof window.__initial_auth_token !== 'undefined' ? window.__initial_auth_token : null;
                    if (token) {
                        await signInWithCustomToken(auth, token);
                    } else {
                        await signInAnonymously(auth);
                    }
                } catch (error) {
                    console.error("Authentication failed:", error);
                    setLoading(false); 
                }
            }
        });

        return () => unsubscribe();
    }, []);

    const renderPage = () => {
        const props = { userId, theme: profile.theme };
        switch (page) {
            case 'dashboard': return <Dashboard {...props} />;
            case 'compliance': return <ComplianceManagement {...props} />;
            case 'assets': return <AssetManagement {...props} />;
            case 'emergency': return <EmergencyPreparedness {...props} />;
            default: return <Dashboard {...props} />;
        }
    };
    
    const isDark = profile.theme === 'dark';

    if (loading) {
        return <div className={`flex items-center justify-center h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}><div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2" style={{borderColor: COLORS.primary}}></div></div>;
    }

    return (
        <div className={`flex h-screen font-sans ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
            <Sidebar setPage={setPage} currentPage={page} userId={userId} profile={profile} />
            <main className="flex-1 p-4 md:p-8 overflow-y-auto">
                {userId ? renderPage() : <div className={`text-center font-bold ${isDark ? 'text-white' : 'text-black'}`}>Authentication failed. Please refresh.</div>}
            </main>
        </div>
    );
}

// --- Sidebar Component ---
const Sidebar = ({ setPage, currentPage, userId, profile }) => {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const navItems = [
        { id: 'dashboard', label: 'Dashboard', icon: ICONS.dashboard },
        { id: 'compliance', label: 'Compliance', icon: ICONS.compliance },
        { id: 'assets', label: 'Assets & Audits', icon: ICONS.assets },
        { id: 'emergency', label: 'Emergency', icon: ICONS.emergency },
    ];
    const isDark = profile.theme === 'dark';

    return (
        <nav className={`w-20 md:w-64 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg flex flex-col justify-between relative`}>
            <div>
                <div className={`flex items-center justify-center md:justify-start p-4 h-20 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-bold text-lg overflow-hidden" style={{ color: COLORS.primary }}>
                        {profile.photoURL ? <img src={profile.photoURL} alt="User" className="w-full h-full object-cover" /> : (profile.firstName?.charAt(0) || 'F')}
                    </div>
                    <h1 className={`hidden md:block ml-3 text-2xl font-bold ${isDark ? 'text-white' : 'text-black'}`}>FSW Comply</h1>
                </div>
                <ul>
                    {navItems.map(item => (
                        <li key={item.id}
                            onClick={() => setPage(item.id)}
                            className={`flex items-center p-4 my-2 mx-2 rounded-lg cursor-pointer transition-all duration-300 ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-200'} ${currentPage === item.id ? 'text-white font-bold' : ''}`}
                            style={{ backgroundColor: currentPage === item.id ? COLORS.primary : 'transparent' }}>
                            <Icon path={item.icon} className="w-6 h-6" />
                            <span className="hidden md:inline ml-4">{item.label}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div className={`p-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <button onClick={() => setSettingsOpen(!settingsOpen)} className={`w-full flex items-center p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'} transition-colors`}>
                    <Icon path={ICONS.user} className={`w-6 h-6 ${isDark ? 'text-gray-300' : 'text-gray-800'}`}/>
                    <div className={`hidden md:block ml-3 text-left ${isDark ? 'text-white' : 'text-black'}`}>
                        <p className="text-sm font-medium truncate">{profile.displayNameType === 'firstName' ? profile.firstName : 'User ID'}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate`} title={userId}>{profile.displayNameType === 'firstName' && userId ? `@${userId.substring(0,8)}...` : profile.firstName}</p>
                    </div>
                </button>
            </div>
            {settingsOpen && <SettingsDropdown userId={userId} profile={profile} onClose={() => setSettingsOpen(false)} />}
        </nav>
    );
};

// --- Settings Dropdown Component ---
const SettingsDropdown = ({ userId, profile, onClose }) => {
    const [localProfile, setLocalProfile] = useState(profile);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const dropdownRef = useRef(null);
    const isDark = localProfile.theme === 'dark';

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);
    
    const handleSave = async () => {
        const profileDocRef = doc(db, `artifacts/${appId}/users/${userId}/profile/info`);
        try {
            await setDoc(profileDocRef, localProfile, { merge: true });
            if(auth.currentUser.photoURL !== localProfile.photoURL) {
                await updateProfile(auth.currentUser, { photoURL: localProfile.photoURL });
            }
            onClose();
        } catch (error) {
            console.error("Error saving profile:", error);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLocalProfile({...localProfile, photoURL: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <>
            <div ref={dropdownRef} className={`absolute bottom-20 mb-2 left-2 right-2 ${isDark ? 'bg-gray-900' : 'bg-white'} rounded-lg shadow-2xl p-4 z-10 border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h4 className={`font-bold mb-4 text-center ${isDark ? 'text-white' : 'text-black'}`}>Settings</h4>
                <div className="space-y-3">
                    <div className="relative w-24 h-24 mx-auto mb-4 group">
                        <img src={localProfile.photoURL || `https://placehold.co/96x96/${isDark ? '1F2937' : 'E5E7EB'}/${isDark ? 'E5E7EB' : '1F2937'}?text=${localProfile.firstName?.charAt(0) || 'U'}`} alt="Profile" className="w-full h-full rounded-full object-cover" />
                        <label htmlFor="photo-upload" className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                            <Icon path={ICONS.camera} className="w-8 h-8" />
                        </label>
                        <input type="file" id="photo-upload" className="hidden" accept="image/*" onChange={handleFileChange} />
                    </div>
                    <input type="text" placeholder="First Name" value={localProfile.firstName} onChange={e => setLocalProfile({...localProfile, firstName: e.target.value})} className={`w-full p-2 rounded ${isDark ? 'bg-gray-800 text-white' : 'bg-gray-200 text-black'}`} />
                    <input type="text" placeholder="Last Name" value={localProfile.lastName} onChange={e => setLocalProfile({...localProfile, lastName: e.target.value})} className={`w-full p-2 rounded ${isDark ? 'bg-gray-800 text-white' : 'bg-gray-200 text-black'}`} />
                    <input type="text" placeholder="Company Name (Optional)" value={localProfile.companyName} onChange={e => setLocalProfile({...localProfile, companyName: e.target.value})} className={`w-full p-2 rounded ${isDark ? 'bg-gray-800 text-white' : 'bg-gray-200 text-black'}`} />
                    <button onClick={() => setShowPasswordModal(true)} className={`w-full p-2 rounded text-sm ${isDark ? 'bg-gray-700 text-white' : 'bg-gray-300'}`}>Change Password</button>
                    
                    <ToggleSwitch label="Dark Mode" isEnabled={isDark} onToggle={() => setLocalProfile({...localProfile, theme: isDark ? 'light' : 'dark'})} theme={localProfile.theme} />
                    <ToggleSwitch label="Show First Name" isEnabled={localProfile.displayNameType === 'firstName'} onToggle={() => setLocalProfile({...localProfile, displayNameType: localProfile.displayNameType === 'firstName' ? 'userId' : 'firstName'})} theme={localProfile.theme} />

                </div>
                <button onClick={handleSave} style={{backgroundColor: COLORS.primary}} className="w-full mt-4 py-2 text-white font-bold rounded-lg hover:opacity-90">Save</button>
            </div>
            {showPasswordModal && <PasswordChangeModal theme={isDark ? 'dark' : 'light'} onClose={() => setShowPasswordModal(false)} />}
        </>
    );
};

const PasswordChangeModal = ({ theme, onClose }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const isDark = theme === 'dark';

    const handleChangePassword = async () => {
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (newPassword.length < 6) {
            setError("Password should be at least 6 characters.");
            return;
        }
        setError('');
        setSuccess('');

        try {
            const user = auth.currentUser;
            // Note: For providers other than email/password, this will fail without re-authentication.
            // This implementation is simplified for the MVP.
            await updatePassword(user, newPassword);
            setSuccess("Password updated successfully!");
            setTimeout(onClose, 2000);
        } catch (err) {
            setError("Failed to update password. You may need to sign in again.");
            console.error(err);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
            <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-2xl p-8 w-full max-w-md`}>
                <h3 className="text-2xl font-bold mb-4">Change Password</h3>
                <div className="space-y-4">
                    <input type="password" placeholder="New Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} />
                    <input type="password" placeholder="Confirm New Password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} />
                </div>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                {success && <p className="text-green-500 text-sm mt-2">{success}</p>}
                <div className="mt-8 flex justify-end space-x-4">
                    <button type="button" onClick={onClose} className="py-2 px-4 bg-gray-600 hover:bg-gray-500 rounded-lg text-white font-semibold">Cancel</button>
                    <button onClick={handleChangePassword} style={{backgroundColor: COLORS.primary}} className="py-2 px-4 hover:opacity-90 rounded-lg text-white font-semibold">Update</button>
                </div>
            </div>
        </div>
    );
};


const ToggleSwitch = ({ label, isEnabled, onToggle, theme }) => (
    <div className="flex items-center justify-between">
        <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{label}</span>
        <button onClick={onToggle} className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${isEnabled ? 'bg-green-500' : (theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300')}`}>
            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    </div>
);


// --- Dashboard Component ---
const Dashboard = ({ userId, theme }) => {
    const [stats, setStats] = useState({ compliance: 0, assets: 0, overdue: 0, upcoming: 0 });
    const [complianceData, setComplianceData] = useState([]);
    const [assetData, setAssetData] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const isDark = theme === 'dark';

    useEffect(() => {
        if (!userId) return;

        const complianceUnsub = onSnapshot(collection(db, `artifacts/${appId}/users/${userId}/compliance`), snapshot => {
            const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setComplianceData(docs);
            const upcoming = docs.filter(d => new Date(d.renewalDate) > new Date() && (new Date(d.renewalDate) - new Date()) / (1000 * 60 * 60 * 24) <= 30).length;
            setStats(prev => ({ ...prev, compliance: docs.length, upcoming }));
            
            const newAlerts = docs
                .filter(d => new Date(d.renewalDate) < new Date())
                .map(d => ({ type: 'Overdue Compliance', text: `${d.documentName} was due on ${new Date(d.renewalDate).toLocaleDateString()}`, level: 'high' }));
            setAlerts(prev => [...prev.filter(a => a.type !== 'Overdue Compliance'), ...newAlerts]);
        }, error => console.error("Compliance snapshot error:", error));

        const assetsUnsub = onSnapshot(collection(db, `artifacts/${appId}/users/${userId}/assets`), snapshot => {
            const assets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAssetData(assets);
            setStats(prev => ({ ...prev, assets: assets.length }));
        }, error => console.error("Assets snapshot error:", error));

        const auditsUnsub = onSnapshot(collection(db, `artifacts/${appId}/users/${userId}/audits`), snapshot => {
            const overdueAudits = snapshot.docs
                .filter(doc => doc.data().status !== 'Completed' && new Date(doc.data().dueDate) < new Date())
                .length;
            setStats(prev => ({ ...prev, overdue: overdueAudits }));

            const newAlerts = snapshot.docs
                .filter(doc => doc.data().status !== 'Completed' && new Date(doc.data().dueDate) < new Date())
                .map(d => ({ type: 'Overdue Audit', text: `Audit for ${d.data().assetName} was due on ${new Date(d.data().dueDate).toLocaleDateString()}`, level: 'high' }));
            setAlerts(prev => [...prev.filter(a => a.type !== 'Overdue Audit'), ...newAlerts]);

        }, error => console.error("Audits snapshot error:", error));

        return () => {
            complianceUnsub();
            assetsUnsub();
            auditsUnsub();
        };
    }, [userId]);

    const complianceChartData = {
        labels: ['Compliant', 'Overdue'],
        datasets: [{
            data: [
                complianceData.filter(d => new Date(d.renewalDate) >= new Date()).length,
                complianceData.filter(d => new Date(d.renewalDate) < new Date()).length
            ],
            backgroundColor: [COLORS.secondary, COLORS.accent],
            borderColor: [isDark ? 'rgba(17, 24, 39, 1)' : 'rgba(243, 244, 246, 1)'],
            borderWidth: 4,
        }]
    };

    const assetStatusChartData = {
        labels: ASSET_TYPES,
        datasets: [{
            label: 'Asset Count',
            data: ASSET_TYPES.map(type => assetData.filter(a => a.assetType === type).length),
            backgroundColor: COLORS.primary,
        }]
    };

    const chartOptions = (title) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: title, color: isDark ? 'white' : 'black', font: { size: 18 } },
            legend: { labels: { color: isDark ? 'white' : 'black' } },
        },
        scales: {
            y: { ticks: { color: isDark ? '#d1d5db' : '#374151' }, grid: { color: isDark ? '#4b5563' : '#e5e7eb' } },
            x: { ticks: { color: isDark ? '#d1d5db' : '#374151' }, grid: { color: isDark ? '#4b5563' : '#e5e7eb' } }
        }
    });

    const doughnutOptions = (title) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: title, color: isDark ? 'white' : 'black', font: { size: 18 } },
            legend: { position: 'top', labels: { color: isDark ? 'white' : 'black' } },
        }
    });

    return (
        <div className={isDark ? 'text-white' : 'text-black'}>
            <h2 className="text-3xl font-bold mb-6">Dashboard</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <StatCard title="Compliance Docs" value={stats.compliance} theme={theme} />
                <StatCard title="Assets Monitored" value={stats.assets} theme={theme} />
                <StatCard title="Overdue Tasks" value={stats.overdue} color={COLORS.accent} theme={theme} />
                <StatCard title="Upcoming Renewals" value={stats.upcoming} color="#FBBF24" theme={theme} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className={`lg:col-span-3 ${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-lg`}>
                    <Bar data={assetStatusChartData} options={chartOptions('Asset Distribution')} height={300} />
                </div>
                <div className={`lg:col-span-2 ${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-lg`}>
                    <Doughnut data={complianceChartData} options={doughnutOptions('Compliance Status')} height={300} />
                </div>
            </div>
            <div className={`mt-6 ${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-lg`}>
                <h3 className="text-xl font-semibold mb-4">High-Priority Alerts</h3>
                <div className="space-y-3">
                    {alerts.length > 0 ? alerts.map((alert, index) => (
                        <div key={index} className={`p-3 rounded-md flex items-center`} style={{backgroundColor: alert.level === 'high' ? COLORS.accent+'4D' : '#FBBF244D'}}>
                            <span className={`font-bold mr-3`} style={{color: alert.level === 'high' ? COLORS.accent : '#FBBF24'}}>{alert.type}:</span>
                            <span className={isDark ? 'text-gray-300' : 'text-gray-800'}>{alert.text}</span>
                        </div>
                    )) : <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No high-priority alerts. Systems are nominal.</p>}
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ title, value, color, theme }) => {
    const isDark = theme === 'dark';
    return (
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow-lg`}>
            <h4 className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-sm font-medium`}>{title}</h4>
            <p className="text-3xl font-bold mt-2" style={{color: color || COLORS.primary}}>{value}</p>
        </div>
    );
};

// --- Other Components (Compliance, Assets, Emergency) ---
const ComplianceManagement = ({ userId, theme }) => {
    const [documents, setDocuments] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentDoc, setCurrentDoc] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const isDark = theme === 'dark';

    useEffect(() => {
        if (!userId) return;
        const q = query(collection(db, `artifacts/${appId}/users/${userId}/compliance`));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setDocuments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [userId]);

    const handleSave = async (docData) => {
        if (currentDoc) {
            await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/compliance`, currentDoc.id), docData);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/compliance`), docData);
        }
        setShowModal(false);
        setCurrentDoc(null);
    };

    const handleDeleteRequest = (id) => {
        setItemToDelete(id);
        setShowConfirmModal(true);
    };

    const confirmDelete = async () => {
        if (itemToDelete) {
            await deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/compliance`, itemToDelete));
        }
        setShowConfirmModal(false);
        setItemToDelete(null);
    };

    const openModal = (doc = null) => {
        setCurrentDoc(doc);
        setShowModal(true);
    };

    return (
        <div className={isDark ? 'text-white' : 'text-black'}>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Compliance Management</h2>
                <button onClick={() => openModal()} style={{backgroundColor: COLORS.primary}} className="hover:opacity-90 text-white font-bold py-2 px-4 rounded-lg flex items-center">
                    <Icon path={ICONS.add} className="w-5 h-5 mr-2" /> Add Document
                </button>
            </div>
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg overflow-hidden`}>
                <table className="min-w-full">
                    <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <tr>
                            <th className="p-4 text-left text-sm font-semibold">Document Name</th>
                            <th className="p-4 text-left text-sm font-semibold">Regulation</th>
                            <th className="p-4 text-left text-sm font-semibold">Renewal Date</th>
                            <th className="p-4 text-left text-sm font-semibold">Status</th>
                            <th className="p-4 text-left text-sm font-semibold">Actions</th>
                        </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                        {documents.map(doc => {
                            const isOverdue = new Date(doc.renewalDate) < new Date();
                            return (
                                <tr key={doc.id} className={`${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                                    <td className="p-4 whitespace-nowrap">{doc.documentName}</td>
                                    <td className="p-4 whitespace-nowrap">{doc.regulation}</td>
                                    <td className="p-4 whitespace-nowrap">{new Date(doc.renewalDate).toLocaleDateString()}</td>
                                    <td className="p-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full text-white`} style={{backgroundColor: isOverdue ? COLORS.accent : COLORS.secondary}}>
                                            {isOverdue ? 'Overdue' : 'Compliant'}
                                        </span>
                                    </td>
                                    <td className="p-4 whitespace-nowrap">
                                        <button onClick={() => openModal(doc)} className="mr-4 hover:opacity-80" style={{color: COLORS.primary}}><Icon path={ICONS.edit} /></button>
                                        <button onClick={() => handleDeleteRequest(doc.id)} className="hover:opacity-80" style={{color: COLORS.accent}}><Icon path={ICONS.delete} /></button>
                                    </td>
                                </tr>
                            );
                        })}
                         {documents.length === 0 && (
                            <tr>
                                <td colSpan="5" className="text-center p-8">No compliance documents found. Add one to get started.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {showModal && <ComplianceModal doc={currentDoc} onSave={handleSave} onClose={() => setShowModal(false)} theme={theme} />}
            <ConfirmationModal 
                show={showConfirmModal}
                onConfirm={confirmDelete}
                onCancel={() => setShowConfirmModal(false)}
                title="Confirm Deletion"
                message="Are you sure you want to delete this document?"
                theme={theme}
            />
        </div>
    );
};

const AssetManagement = ({ userId, theme }) => {
    const [assets, setAssets] = useState([]);
    const [audits, setAudits] = useState([]);
    const [view, setView] = useState('assets');
    const [showAssetModal, setShowAssetModal] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [currentAsset, setCurrentAsset] = useState(null);
    const [currentAudit, setCurrentAudit] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const isDark = theme === 'dark';

    useEffect(() => {
        if (!userId) return;
        const assetsUnsub = onSnapshot(collection(db, `artifacts/${appId}/users/${userId}/assets`), (snapshot) => {
            setAssets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        const auditsUnsub = onSnapshot(collection(db, `artifacts/${appId}/users/${userId}/audits`), (snapshot) => {
            setAudits(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => {
            assetsUnsub();
            auditsUnsub();
        };
    }, [userId]);

    const handleSaveAsset = async (assetData) => {
        if (currentAsset) {
            await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/assets`, currentAsset.id), assetData);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/assets`), { ...assetData, uniqueId: `FSW-${Date.now()}` });
        }
        setShowAssetModal(false);
        setCurrentAsset(null);
    };

    const handleDeleteRequest = (id, type) => {
        setItemToDelete({ id, type });
        setShowConfirmModal(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        const { id, type } = itemToDelete;

        if (type === 'asset') {
            const batch = writeBatch(db);
            batch.delete(doc(db, `artifacts/${appId}/users/${userId}/assets`, id));
            const assetName = assets.find(a => a.id === id)?.name;
            if (assetName) {
                const relatedAudits = audits.filter(a => a.assetName === assetName);
                relatedAudits.forEach(audit => {
                    batch.delete(doc(db, `artifacts/${appId}/users/${userId}/audits`, audit.id));
                });
            }
            await batch.commit();
        } else if (type === 'audit') {
            await deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/audits`, id));
        }

        setShowConfirmModal(false);
        setItemToDelete(null);
    };

    const handleSaveAudit = async (auditData) => {
        if (currentAudit) {
            await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/audits`, currentAudit.id), auditData);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/audits`), auditData);
        }
        setShowAuditModal(false);
        setCurrentAudit(null);
    };

    return (
        <div className={isDark ? 'text-white' : 'text-black'}>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Assets & Audits</h2>
                <div>
                    <button onClick={() => setView('assets')} className={`mr-2 py-2 px-4 rounded-lg font-semibold ${view === 'assets' ? 'text-white' : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')}`} style={{backgroundColor: view === 'assets' ? COLORS.primary : ''}}>Assets</button>
                    <button onClick={() => setView('audits')} className={`py-2 px-4 rounded-lg font-semibold ${view === 'audits' ? 'text-white' : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')}`} style={{backgroundColor: view === 'audits' ? COLORS.primary : ''}}>Audits</button>
                </div>
                <button onClick={() => view === 'assets' ? setShowAssetModal(true) : setShowAuditModal(true)} style={{backgroundColor: COLORS.primary}} className="hover:opacity-90 text-white font-bold py-2 px-4 rounded-lg flex items-center">
                    <Icon path={ICONS.add} className="w-5 h-5 mr-2" /> Add {view === 'assets' ? 'Asset' : 'Audit'}
                </button>
            </div>

            {view === 'assets' ? (
                <AssetsTable assets={assets} onEdit={(asset) => { setCurrentAsset(asset); setShowAssetModal(true); }} onDelete={(id) => handleDeleteRequest(id, 'asset')} theme={theme} />
            ) : (
                <AuditsTable audits={audits} onEdit={(audit) => { setCurrentAudit(audit); setShowAuditModal(true); }} onDelete={(id) => handleDeleteRequest(id, 'audit')} theme={theme} />
            )}

            {showAssetModal && <AssetModal asset={currentAsset} onSave={handleSaveAsset} onClose={() => { setShowAssetModal(false); setCurrentAsset(null); }} theme={theme} />}
            {showAuditModal && <AuditModal audit={currentAudit} assets={assets} onSave={handleSaveAudit} onClose={() => { setShowAuditModal(false); setCurrentAudit(null); }} theme={theme} />}
            <ConfirmationModal 
                show={showConfirmModal}
                onConfirm={confirmDelete}
                onCancel={() => setShowConfirmModal(false)}
                title="Confirm Deletion"
                message={`Are you sure you want to delete this ${itemToDelete?.type}? ${itemToDelete?.type === 'asset' ? 'This will also delete related audits.' : ''}`}
                theme={theme}
            />
        </div>
    );
};

const AssetsTable = ({ assets, onEdit, onDelete, theme }) => {
    const isDark = theme === 'dark';
    return (
    <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg overflow-x-auto`}>
        <table className="min-w-full">
            <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                    <th className="p-4 text-left text-sm font-semibold">Asset Name</th>
                    <th className="p-4 text-left text-sm font-semibold">Type</th>
                    <th className="p-4 text-left text-sm font-semibold">Location</th>
                    <th className="p-4 text-left text-sm font-semibold">Install Date</th>
                    <th className="p-4 text-left text-sm font-semibold">Status</th>
                    <th className="p-4 text-left text-sm font-semibold">Actions</th>
                </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {assets.map(asset => (
                    <tr key={asset.id} className={`${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                        <td className="p-4 whitespace-nowrap">{asset.name}</td>
                        <td className="p-4 whitespace-nowrap">{asset.assetType}</td>
                        <td className="p-4 whitespace-nowrap">{asset.location}</td>
                        <td className="p-4 whitespace-nowrap">{new Date(asset.installDate).toLocaleDateString()}</td>
                        <td className="p-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full text-white`} style={{backgroundColor: asset.status === 'Operational' ? COLORS.secondary : COLORS.accent}}>
                                {asset.status}
                            </span>
                        </td>
                        <td className="p-4 whitespace-nowrap">
                            <button onClick={() => onEdit(asset)} className="mr-4 hover:opacity-80" style={{color: COLORS.primary}}><Icon path={ICONS.edit} /></button>
                            <button onClick={() => onDelete(asset.id)} className="hover:opacity-80" style={{color: COLORS.accent}}><Icon path={ICONS.delete} /></button>
                        </td>
                    </tr>
                ))}
                {assets.length === 0 && (
                    <tr>
                        <td colSpan="6" className="text-center p-8">No assets found. Add one to get started.</td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
    );
};

const AuditsTable = ({ audits, onEdit, onDelete, theme }) => {
    const isDark = theme === 'dark';
    return (
    <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg overflow-x-auto`}>
        <table className="min-w-full">
            <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                    <th className="p-4 text-left text-sm font-semibold">Asset Name</th>
                    <th className="p-4 text-left text-sm font-semibold">Due Date</th>
                    <th className="p-4 text-left text-sm font-semibold">Auditor</th>
                    <th className="p-4 text-left text-sm font-semibold">Status</th>
                    <th className="p-4 text-left text-sm font-semibold">Actions</th>
                </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {audits.map(audit => {
                    const isOverdue = new Date(audit.dueDate) < new Date() && audit.status !== 'Completed';
                    return (
                        <tr key={audit.id} className={`${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                            <td className="p-4 whitespace-nowrap">{audit.assetName}</td>
                            <td className="p-4 whitespace-nowrap">{new Date(audit.dueDate).toLocaleDateString()}</td>
                            <td className="p-4 whitespace-nowrap">{audit.auditor}</td>
                            <td className="p-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full text-white`} style={{backgroundColor: isOverdue ? COLORS.accent : (audit.status === 'Completed' ? COLORS.secondary : '#FBBF24')}}>
                                    {isOverdue ? 'Overdue' : audit.status}
                                </span>
                            </td>
                            <td className="p-4 whitespace-nowrap">
                                <button onClick={() => onEdit(audit)} className="mr-4 hover:opacity-80" style={{color: COLORS.primary}}><Icon path={ICONS.edit} /></button>
                                <button onClick={() => onDelete(audit.id)} className="hover:opacity-80" style={{color: COLORS.accent}}><Icon path={ICONS.delete} /></button>
                            </td>
                        </tr>
                    );
                })}
                {audits.length === 0 && (
                    <tr>
                        <td colSpan="5" className="text-center p-8">No audits scheduled. Add one to get started.</td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
    );
};

const AssetModal = ({ asset, onSave, onClose, theme }) => {
    const [formData, setFormData] = useState({
        name: asset?.name || '',
        assetType: asset?.assetType || ASSET_TYPES[0],
        location: asset?.location || '',
        installDate: asset?.installDate || '',
        status: asset?.status || 'Operational',
    });
    const isDark = theme === 'dark';

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
            <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-2xl p-8 w-full max-w-lg`}>
                <h3 className="text-2xl font-bold mb-6">{asset ? 'Edit' : 'Add'} Asset</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Asset Name" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <select name="assetType" value={formData.assetType} onChange={handleChange} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}>
                        {ASSET_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                    <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="Location (e.g., 2nd Floor, West Wing)" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <input type="date" name="installDate" value={formData.installDate} onChange={handleChange} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <select name="status" value={formData.status} onChange={handleChange} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}>
                        <option value="Operational">Operational</option>
                        <option value="Under Maintenance">Under Maintenance</option>
                        <option value="Decommissioned">Decommissioned</option>
                    </select>
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={onClose} className="py-2 px-4 bg-gray-600 hover:bg-gray-500 rounded-lg text-white font-semibold">Cancel</button>
                        <button type="submit" style={{backgroundColor: COLORS.primary}} className="py-2 px-4 hover:opacity-90 rounded-lg text-white font-semibold">Save Asset</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const AuditModal = ({ audit, assets, onSave, onClose, theme }) => {
    const [formData, setFormData] = useState({
        assetName: audit?.assetName || (assets.length > 0 ? assets[0].name : ''),
        dueDate: audit?.dueDate || '',
        auditor: audit?.auditor || '',
        status: audit?.status || 'Scheduled',
        notes: audit?.notes || '',
    });
    const isDark = theme === 'dark';

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
            <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-2xl p-8 w-full max-w-lg`}>
                <h3 className="text-2xl font-bold mb-6">{audit ? 'Edit' : 'Schedule'} Audit</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <select name="assetName" value={formData.assetName} onChange={handleChange} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required disabled={assets.length === 0}>
                        {assets.length > 0 ? assets.map(asset => <option key={asset.id} value={asset.name}>{asset.name}</option>) : <option>No assets available</option>}
                    </select>
                    <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <input type="text" name="auditor" value={formData.auditor} onChange={handleChange} placeholder="Auditor Name" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <select name="status" value={formData.status} onChange={handleChange} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}>
                        <option value="Scheduled">Scheduled</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                    </select>
                    <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Audit Notes..." rows="3" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}></textarea>
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={onClose} className="py-2 px-4 bg-gray-600 hover:bg-gray-500 rounded-lg text-white font-semibold">Cancel</button>
                        <button type="submit" style={{backgroundColor: COLORS.primary}} className="py-2 px-4 hover:opacity-90 rounded-lg text-white font-semibold" disabled={assets.length === 0}>Save Audit</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const EmergencyPreparedness = ({ userId, theme }) => {
    const [plans, setPlans] = useState([]);
    const [drills, setDrills] = useState([]);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [showDrillModal, setShowDrillModal] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [currentDrill, setCurrentDrill] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const isDark = theme === 'dark';

    useEffect(() => {
        if (!userId) return;
        const plansUnsub = onSnapshot(collection(db, `artifacts/${appId}/users/${userId}/plans`), (snapshot) => {
            setPlans(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        const drillsUnsub = onSnapshot(collection(db, `artifacts/${appId}/users/${userId}/drills`), (snapshot) => {
            setDrills(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => {
            plansUnsub();
            drillsUnsub();
        };
    }, [userId]);

    const handleSavePlan = async (data) => {
        if (currentPlan) {
            await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/plans`, currentPlan.id), data);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/plans`), data);
        }
        setShowPlanModal(false);
        setCurrentPlan(null);
    };

    const handleDeleteRequest = (id, type) => {
        setItemToDelete({ id, type });
        setShowConfirmModal(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        const { id, type } = itemToDelete;
        const collectionName = type === 'plan' ? 'plans' : 'drills';
        await deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/${collectionName}`, id));
        setShowConfirmModal(false);
        setItemToDelete(null);
    };

    const handleSaveDrill = async (data) => {
        if (currentDrill) {
            await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/drills`, currentDrill.id), data);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/drills`), data);
        }
        setShowDrillModal(false);
        setCurrentDrill(null);
    };

    return (
        <div className={isDark ? 'text-white' : 'text-black'}>
            <h2 className="text-3xl font-bold mb-6">Emergency Preparedness</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold">Evacuation Plans</h3>
                        <button onClick={() => { setCurrentPlan(null); setShowPlanModal(true); }} style={{backgroundColor: COLORS.primary}} className="hover:opacity-90 text-white font-bold py-2 px-4 rounded-lg flex items-center text-sm">
                            <Icon path={ICONS.add} className="w-4 h-4 mr-2" /> Add Plan
                        </button>
                    </div>
                    <div className="space-y-3">
                        {plans.length > 0 ? plans.map(plan => (
                            <div key={plan.id} className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-3 rounded-md flex justify-between items-center`}>
                                <div>
                                    <p className="font-semibold">{plan.planName}</p>
                                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Assembly Point: {plan.assemblyPoint}</p>
                                </div>
                                <div>
                                    <button onClick={() => { setCurrentPlan(plan); setShowPlanModal(true); }} className="mr-4 hover:opacity-80" style={{color: COLORS.primary}}><Icon path={ICONS.edit} /></button>
                                    <button onClick={() => handleDeleteRequest(plan.id, 'plan')} className="hover:opacity-80" style={{color: COLORS.accent}}><Icon path={ICONS.delete} /></button>
                                </div>
                            </div>
                        )) : <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-center p-4`}>No evacuation plans created.</p>}
                    </div>
                </div>
                <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold">Fire Drills</h3>
                        <button onClick={() => { setCurrentDrill(null); setShowDrillModal(true); }} style={{backgroundColor: COLORS.primary}} className="hover:opacity-90 text-white font-bold py-2 px-4 rounded-lg flex items-center text-sm">
                            <Icon path={ICONS.add} className="w-4 h-4 mr-2" /> Log Drill
                        </button>
                    </div>
                    <div className="space-y-3">
                        {drills.length > 0 ? drills.map(drill => (
                            <div key={drill.id} className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-3 rounded-md flex justify-between items-center`}>
                                <div>
                                    <p className="font-semibold">Drill on {new Date(drill.drillDate).toLocaleDateString()}</p>
                                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{drill.participants} participants. Evacuation time: {drill.evacuationTime} mins.</p>
                                </div>
                                <div>
                                    <button onClick={() => { setCurrentDrill(drill); setShowDrillModal(true); }} className="mr-4 hover:opacity-80" style={{color: COLORS.primary}}><Icon path={ICONS.edit} /></button>
                                    <button onClick={() => handleDeleteRequest(drill.id, 'drill')} className="hover:opacity-80" style={{color: COLORS.accent}}><Icon path={ICONS.delete} /></button>
                                </div>
                            </div>
                        )) : <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-center p-4`}>No fire drills logged.</p>}
                    </div>
                </div>
            </div>
            <div className={`mt-8 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
                <h3 className="text-xl font-semibold mb-4">Emergency Contacts</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                     <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-4 rounded-md`}>
                        <p className="font-bold" style={{color: COLORS.accent}}>Fire Brigade</p>
                        <p className="text-2xl font-mono">101</p>
                    </div>
                    <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-4 rounded-md`}>
                        <p className="font-bold" style={{color: COLORS.primary}}>Police</p>
                        <p className="text-2xl font-mono">100</p>
                    </div>
                    <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-4 rounded-md`}>
                        <p className="font-bold" style={{color: COLORS.secondary}}>Ambulance</p>
                        <p className="text-2xl font-mono">102</p>
                    </div>
                </div>
            </div>
            {showPlanModal && <PlanModal plan={currentPlan} onSave={handleSavePlan} onClose={() => setShowPlanModal(false)} theme={theme} />}
            {showDrillModal && <DrillModal drill={currentDrill} onSave={handleSaveDrill} onClose={() => setShowDrillModal(false)} theme={theme} />}
            <ConfirmationModal 
                show={showConfirmModal}
                onConfirm={confirmDelete}
                onCancel={() => setShowConfirmModal(false)}
                title="Confirm Deletion"
                message={`Are you sure you want to delete this ${itemToDelete?.type}?`}
                theme={theme}
            />
        </div>
    );
};

const PlanModal = ({ plan, onSave, onClose, theme }) => {
    const [formData, setFormData] = useState({
        planName: plan?.planName || '',
        assemblyPoint: plan?.assemblyPoint || '',
        planUrl: plan?.planUrl || '',
    });
    const isDark = theme === 'dark';

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
            <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-2xl p-8 w-full max-w-lg`}>
                <h3 className="text-2xl font-bold mb-6">{plan ? 'Edit' : 'Create'} Evacuation Plan</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" name="planName" value={formData.planName} onChange={e => setFormData({...formData, planName: e.target.value})} placeholder="Plan Name (e.g., Main Office Plan)" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <input type="text" name="assemblyPoint" value={formData.assemblyPoint} onChange={e => setFormData({...formData, assemblyPoint: e.target.value})} placeholder="Assembly Point Location" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <input type="text" name="planUrl" value={formData.planUrl} onChange={e => setFormData({...formData, planUrl: e.target.value})} placeholder="URL to Floor Plan Image (Optional)" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} />
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={onClose} className="py-2 px-4 bg-gray-600 hover:bg-gray-500 rounded-lg text-white font-semibold">Cancel</button>
                        <button type="submit" style={{backgroundColor: COLORS.primary}} className="py-2 px-4 hover:opacity-90 rounded-lg text-white font-semibold">Save Plan</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const DrillModal = ({ drill, onSave, onClose, theme }) => {
    const [formData, setFormData] = useState({
        drillDate: drill?.drillDate || '',
        participants: drill?.participants || '',
        evacuationTime: drill?.evacuationTime || '',
        observations: drill?.observations || '',
    });
    const isDark = theme === 'dark';

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
            <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-2xl p-8 w-full max-w-lg`}>
                <h3 className="text-2xl font-bold mb-6">{drill ? 'Edit' : 'Log'} Fire Drill</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="date" name="drillDate" value={formData.drillDate} onChange={e => setFormData({...formData, drillDate: e.target.value})} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <input type="number" name="participants" value={formData.participants} onChange={e => setFormData({...formData, participants: e.target.value})} placeholder="Number of Participants" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <input type="number" name="evacuationTime" value={formData.evacuationTime} onChange={e => setFormData({...formData, evacuationTime: e.target.value})} placeholder="Evacuation Time (in minutes)" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`} required />
                    <textarea name="observations" value={formData.observations} onChange={e => setFormData({...formData, observations: e.target.value})} placeholder="Observations & Notes" rows="3" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}></textarea>
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={onClose} className="py-2 px-4 bg-gray-600 hover:bg-gray-500 rounded-lg text-white font-semibold">Cancel</button>
                        <button type="submit" style={{backgroundColor: COLORS.primary}} className="py-2 px-4 hover:opacity-90 rounded-lg text-white font-semibold">Save Drill</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Modals need theme prop for styling ---
const ComplianceModal = ({ doc, onSave, onClose, theme }) => {
    const [formData, setFormData] = useState({
        documentName: doc?.documentName || '',
        regulation: doc?.regulation || REGULATION_OPTIONS[0],
        renewalDate: doc?.renewalDate || '',
        fileUrl: doc?.fileUrl || '',
    });
    const isDark = theme === 'dark';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
            <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-black'} rounded-lg shadow-2xl p-8 w-full max-w-lg`}>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold">{doc ? 'Edit' : 'Add'} Compliance Document</h3>
                    <button onClick={onClose}><Icon path={ICONS.close} /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <input type="text" name="documentName" value={formData.documentName} onChange={handleChange} placeholder="Document Name (e.g., Fire NOC)" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md border ${isDark ? 'border-gray-600' : 'border-gray-300'} focus:outline-none focus:ring-2`} style={{'--tw-ring-color': COLORS.primary}} required />
                        <select name="regulation" value={formData.regulation} onChange={handleChange} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md border ${isDark ? 'border-gray-600' : 'border-gray-300'} focus:outline-none focus:ring-2`} style={{'--tw-ring-color': COLORS.primary}}>
                            {REGULATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        <input type="date" name="renewalDate" value={formData.renewalDate} onChange={handleChange} className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md border ${isDark ? 'border-gray-600' : 'border-gray-300'} focus:outline-none focus:ring-2`} style={{'--tw-ring-color': COLORS.primary}} required />
                        <input type="text" name="fileUrl" value={formData.fileUrl} onChange={handleChange} placeholder="Document URL (Optional)" className={`w-full p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-md border ${isDark ? 'border-gray-600' : 'border-gray-300'} focus:outline-none focus:ring-2`} style={{'--tw-ring-color': COLORS.primary}} />
                    </div>
                    <div className="mt-8 flex justify-end space-x-4">
                        <button type="button" onClick={onClose} className="py-2 px-4 bg-gray-600 hover:bg-gray-500 rounded-lg text-white font-semibold">Cancel</button>
                        <button type="submit" style={{backgroundColor: COLORS.primary}} className="py-2 px-4 hover:opacity-90 rounded-lg text-white font-semibold">Save Document</button>
                    </div>
                </form>
            </div>
        </div>
    );
};
